import videojs from 'video.js';

import { IMediaPlayer } from './IMediaPlayer';

// import 'videojs-contrib-eme';
import eme from 'videojs-contrib-eme';
import 'videojs-landscape-fullscreen';
import 'videojs-contrib-quality-levels';
import 'videojs-http-source-selector';
import 'videojs-vtt-thumbnails';
import EventEmitter from 'events';

import { Events } from './events/Events';
import { Media } from './configs/Media';
import { createSubtitleButton } from './components/SubtitleButton';
import { addSeekButtons, seek } from './components/SeekButton';
import { AdsManager } from './advertisement/AdsManager';
import { AdClient } from './configs/AdConfig';
import { TextTrack } from './configs/TextTrack';
import { Error, ErrorCategory, ErrorCode, ErrorSeverity } from './error/Error';
// import 'videojs-sprite-thumbnails';
// import { VideoThumNail } from './configs/VideoThumbNail';
import { CastSender } from './cast/CastSender';
import {
  getLanguageName,
  getBrowserType,
  getBitrateSelectedText,
  isMobileDevice,
  isB2BPartner
} from './utils/Utils';
import npawAnalytics from './analytics/npaw/npawAnalytics';

// eslint-disable-next-line import/order, import/named
import { detectAnyAdblocker } from 'just-detect-adblock';

import { AdEvents } from './events/AdEvents';
import { playerEventsConstants } from './constants/playerConstants';
import { PlayerConfig } from './configs/PlayerConfig';
import { TapToUnmute } from './components/TapToUnmute';
import { SpApiResponse } from 'configs/SpApiResponse';

// import canAutoPlay from 'can-autoplay';

declare global {
  interface Window {
    enableReloadPlayerOnTimeout: any;
    clearPlayerReloadTimer: any;
  }
}

export class MediaPlayer extends EventEmitter implements IMediaPlayer {
  absoluteCurrentTime = 0;
  absoluteDuration = 0;
  npawManager!: any;
  adsManager!: AdsManager;
  castSender!: CastSender | null;
  videoPlayer: any;
  videoElement: HTMLElement | null;
  playerConfig: any;
  playerContainer!: HTMLElement | null;
  previousTime = 0;
  currentTime = 0;
  seekStart: number | null = 0;
  selectedQualityLevel = 0;
  mediaConfig!: Media;
  autoPlayFailed = false;
  isPlayingEventSent = false;
  isRequestedByBot = false;

  defaultOptions = {
    autoplay: true,
    muted: true,
    controls: true,
    errorDisplay: false,
    width: 640,
    height: 480,
    html5: {
      vhs: {
        overrideNative: !videojs.browser.IS_SAFARI || false,
        useNetworkInformationApi: true,
        experimentalBufferBasedABR: true
      },
      nativeTextTracks: false,
      nativeAudioTracks: false,
      nativeVideoTracks: false
    },
    controlBar: {
      pictureInPictureToggle: false
    },
    userActions: {
      hotkeys: function (this: any, event: any) {
        event.preventDefault();
        const volumeStep = 0.1;
        // "f key" fullscreen mode toggling
        if (event.which === 70) {
          this.isFullscreen()
            ? this.exitFullscreen()
            : this.requestFullscreen();
        }
        // "space key" or "k" pressed
        if (event.which === 32 || event.which === 75) {
          this.paused() ? this.play() : this.pause();
        }
        // right and left arrows functions when the content is not live
        if (!this.liveTracker.isLive()) {
          // seeking forward on "right arrow" and "l" pressed
          if (event.which === 39 || event.which === 76) {
            seek(10, this);
          }
          // seeking forward on "left arrow" and "j" pressed
          if (event.which === 37 || event.which === 74) {
            seek(-10, this);
          }
        }
        // "up arrow" function for volume up
        if (event.which === 38) {
          if (this.muted()) {
            this.muted(false);
          }
          this.volume(this.volume() + volumeStep);
        }
        // "down arrow" function for volume down
        if (event.which === 40) {
          this.volume(this.volume() - volumeStep);
        }
        // "m" key functionality for muted volume toggling
        if (event.which === 77) {
          this.muted() ? this.muted(false) : this.muted(true);
        }
      }
    }
  };

  // constructor function which accept video element as param
  constructor(videoTagId: Element) {
    super();
    this.videoElement = document.getElementById(videoTagId.toString());
    const browserType = getBrowserType();
    if (this.videoElement) {
      if (browserType === 'Safari' && this.videoElement) {
        this.videoElement.classList.add('isSafariBrowser');
      }
      this.playerContainer = this.videoElement.parentElement;
    }
  }

  // setting up the video player
  setup(
    config: PlayerConfig,
    mediaConfig: Media,
    singlePlayDetails: SpApiResponse,
    userData: any,
    mixpanelData: any,
    isRequestedByBot: boolean = false
  ): void {
    const eventPayload = {};
    this.playerConfig = config;
    this.mediaConfig = mediaConfig;
    this.isRequestedByBot = isRequestedByBot;
    this.selectedQualityLevel = 0;
    if (isMobileDevice()) {
      this.defaultOptions.html5 = {
        ...this.defaultOptions.html5,
        vhs: {
          ...this.defaultOptions.html5.vhs,
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          limitRenditionByPlayerDimensions: false,
          externVhs: {
            GOAL_BUFFER_LENGTH: 15
          }
        } as any
      };
    }
    const options = videojs.mergeOptions(this.defaultOptions, config);
    videojs.registerPlugin('eme', eme);
    if (this.videoElement !== null) {
      this.videoPlayer = videojs(this.videoElement!, options, undefined);
      // videojsHlsjsSourceHandler.register(videojs);
      window.videojs = videojs;
      this.videoPlayer.httpSourceSelector();
      window.videoPlayer = this.videoPlayer;
      this.videoPlayer.landscapeFullscreen({
        fullscreen: {
          enterOnRotate: false,
          exitOnRotate: false,
          alwaysInLandscapeMode: false,
          iOS: false
        }
      });

      npawAnalytics.registerVideoAdapter(
        this.videoPlayer,
        singlePlayDetails,
        userData,
        mixpanelData
      );
      this.adsManager = new AdsManager(this.videoPlayer, this.videoElement!);
      this.castSender = new CastSender(this.videoPlayer, this.adsManager);
      createSubtitleButton(this.videoPlayer);
      this.registerEvents();
      if (!config.sportsLive) {
        this.addSeekButtons();
      }
    } else {
      console.error('Element not found');
      console.log(config, mediaConfig);
      const customErrorEvent = {
        type: 'VJS_ID_ERROR'
      };
      this.sendConvivaReportEvent(customErrorEvent);
    }
  }

  setSource(mediaConfig: Media) {
    this.mediaConfig = mediaConfig;
    detectAnyAdblocker().then((detected: any) => {
      if (!detected && mediaConfig.advertising != null) {
        this.adsManager.setConfig(mediaConfig);
        this.registerAdEvents(this.adsManager);
        if (mediaConfig?.advertising?.client == AdClient.DAI) return;
      } else {
        this.emit(AdEvents.AD_BLOCKER_DETECTED, this.videoPlayer);
      }
      this.videoPlayer.error(null);
      if (this.videoPlayer.eme) {
        this.videoPlayer.eme.activeSrc = null;
      }
      try {
        this.videoPlayer.eme();
      } catch (error: any) {
        const eventPayload = {
          type: 'EME_ERROR',
          payload: error.message
        };
        this.sendConvivaReportEvent(eventPayload);
      }
      this.videoPlayer.src(mediaConfig);
    });
    const eventPayload = {
      type: 'SET_SOURCE_FOR_PLAYER'
    };
    this.sendConvivaReportEvent(eventPayload);
  }

  // resetting the player
  clearSource() {
    if (this.videoPlayer != undefined) {
      if (this.adsManager) this.adsManager.destroy();
      this.videoPlayer.reset();
    }
  }

  // code creates and returns video element
  private getVideoElement(): HTMLMediaElement {
    const video = document.createElement('video') as HTMLMediaElement;
    video.id = 'video-tag';
    video.className = 'playerComp content video-js vjs-big-play-centered';
    video.preload = 'auto';
    video.controls = true;
    video.setAttribute('playsinline', 'true');
    const eventPayload = {
      type: 'CREATE_VIDEO_ELEMENT'
    };
    this.sendConvivaReportEvent(eventPayload);

    return video;
  }

  reset() {
    this.videoPlayer.reset();
    const eventPayload = {
      type: 'VIDEO_PLAYER_RESET'
    };
    this.sendConvivaReportEvent(eventPayload);
  }

  registerAdEvents(adsManager: AdsManager) {
    adsManager.on(AdEvents.AD_STARTED, () => this.emit(AdEvents.AD_STARTED));
    adsManager.on(AdEvents.AD_COMPLETE, () => this.emit(AdEvents.AD_COMPLETE));
    adsManager.on(AdEvents.PRE_ROLL_COMPLETED, () => {
      this.emit(AdEvents.PRE_ROLL_COMPLETED, this.videoPlayer);
    });
    adsManager.on(AdEvents.AD_ERROR, (adError) =>
      this.emit(AdEvents.AD_ERROR, adError)
    );
    adsManager.on(
      AdEvents.STREAM_LOADED,
      this.googleDAIStreamLoaded.bind(this)
    );
    adsManager.on(
      AdEvents.STREAM_LOAD_ERROR,
      this.googleDAIStreamLoadError.bind(this)
    );
    adsManager.on(AdEvents.SKIPPED, () => this.emit(AdEvents.SKIPPED));
  }

  // Google DAI stream loaded listener function
  googleDAIStreamLoaded(streamURL: string) {
    this.mediaConfig.src = streamURL;
    this.videoPlayer.eme();
    this.videoPlayer.src(this.mediaConfig);
  }

  // Google DAI stream load error listener function
  googleDAIStreamLoadError(error: any) {
    this.videoPlayer.src(this.mediaConfig);
  }

  // registering all player relared events
  registerEvents() {
    this.videoPlayer.on('ready', this.onPlayerReady.bind(this));
    this.videoPlayer.on('loadstart', this.onLoadStart.bind(this));
    this.videoPlayer.on('ended', this.onPlayEndedEvent.bind(this));
    this.videoPlayer.on('error', this.onPlayerError.bind(this));
    this.videoPlayer.on('loadeddata', this.onPlayerEvents.bind(this));
    this.videoPlayer.on('loadedmetadata', this.onMetaDataLoaded.bind(this));
    this.videoPlayer.on('timeupdate', this.onTimeUpdate.bind(this));
    this.videoPlayer.on('volumechange', this.onVolumeChange.bind(this));
    this.videoPlayer.on('suspend', this.onPlayerEvents.bind(this));
    this.videoPlayer.on('emptied', this.onPlayerEvents.bind(this));
    this.videoPlayer.on('stalled', this.onPlayerEvents.bind(this));
    this.videoPlayer.on('resize', this.onPlayerEvents.bind(this));
    this.videoPlayer.on('durationchange', this.onDurationChanged.bind(this));
    this.videoPlayer.on('play', this.onPlayEvent.bind(this));
    this.videoPlayer.on('fullscreenchange', this.onFullScreenEvent.bind(this));
    this.videoPlayer.on('seeked', this.onSeekEvent.bind(this));
    this.videoPlayer.on('seeking', this.onSeekingEvent.bind(this));
    this.videoPlayer.on('pause', this.onPauseEvent.bind(this));
    this.videoPlayer.on('subtitleChanged', this.onSubtitleChange.bind(this));
    this.videoPlayer.on('progress', this.onPlayerProgressEvent.bind(this));
    this.videoPlayer.on('dispose', this.onPlayerDisposeEvent.bind(this));
    this.videoPlayer.on('mute', this.onPlayerMute.bind(this));
    this.videoPlayer.on('click', this.onPlayerClick.bind(this));
    this.videoPlayer.on('dblclick', this.onPlayerDblClick.bind(this));
    this.videoPlayer.on('playing', this.onPlayingEvent.bind(this));
    this.videoPlayer.on('useractive', this.userActive.bind(this));
    this.videoPlayer.on('userinactive', this.userInactive.bind(this));
  }

  private showPoster = () => {
    if (this.videoPlayer && this.videoPlayer.posterImage) {
      this.videoPlayer.posterImage.show();
      if (this.videoPlayer.posterImage.el()) {
        this.videoPlayer.posterImage.el().style.display = 'block';
      }
    }
  }

  private onPlayerDblClick(event: any) {
    event.preventDefault();
    if (
      event &&
      event.target &&
      event.target.parentElement &&
      !isMobileDevice() &&
      this.videoPlayer &&
      !isMobileDevice() &&
      event.target.parentElement.classList.contains('playerContainer')
    ) {
      this.videoPlayer.isFullscreen()
        ? this.videoPlayer.exitFullscreen()
        : this.videoPlayer.requestFullscreen();
    }
  }

  private onPlayerClick(event: any) {
    if (event && event.target && event.target.parentElement) {
      if (!isMobileDevice()) {
        if (this.videoPlayer) {
          if (
            event.target.parentElement.classList.contains('playerContainer')
          ) {
            this.videoPlayer.paused() ? this.play() : this.pause();
          }
        }
      }
      console.log(event.target.parentElement.classList.value);
      if (
        event.target.parentElement.classList.value.includes(
          'vjs-http-source-selector'
        )
      ) {
        this.emit(playerEventsConstants.qualityBtnClicked, this.videoPlayer);
      } else if (
        event.target.parentElement.classList.value.includes('vjs-audio-button')
      ) {
        this.emit(playerEventsConstants.audioBtnClicked, this.videoPlayer);
      } else if (
        event.target.parentElement.classList.value.includes(
          'vjs-subtitles-button'
        )
      ) {
        this.emit(playerEventsConstants.subtitleBtnClicked, this.videoPlayer);
      } else if (
        event.target.parentElement.classList.value.includes('vjs-mute-control')
      ) {
        // below removing tap to unmute button on clicking mute toggle
        const tapToUnmuteContainer = document.querySelector('.tap_to_unmute');
        if (tapToUnmuteContainer) {
          document
            .getElementsByClassName('video-js')[0]
            .removeChild(tapToUnmuteContainer);
        }
        this.emit(playerEventsConstants.muteBtnClicked, this.videoPlayer);
      }
    }
  }

  private userActive() {
    this.emit(Events.USER_ACTIVE);
  }

  private userInactive() {
    this.emit(Events.USER_INACTIVE, this.videoPlayer);
  }

  private onPlayingEvent(event: any) {
    if (this.isRequestedByBot) {
      this.videoPlayer.pause();
      this.showPoster();
      return;
    }
    if (!this.isPlayingEventSent) {
      const eventPayload = {
        type: 'FIRST_FRAME_RENDERED'
      };
      // Add tap to unmute btn if the user is mobile user
      const isMobile = /iPhone|iPad|iPod|Android/i.test(
        (window as any).navigator.userAgent
      );

      if (this.videoPlayer.muted() && isB2BPartner() && isMobile) {
        TapToUnmute();
      }
      this.emit(Events.PLAYING, this.videoPlayer);
      this.sendConvivaReportEvent(eventPayload);
      this.isPlayingEventSent = true;
      if (this.autoPlayFailed) {
        this.sendUserWaitEvent(
          window.Conviva?.Constants.Events.USER_WAIT_ENDED || ''
        );
      }
      if (window) {
        window.isPlayingEventSent = true;
        if (window.enableReloadPlayerOnTimeout) {
          window.clearPlayerReloadTimer();
        }
      }
    }
  }

  // videojs dispose listener function
  private onPlayerDisposeEvent() {
    this.emit(Events.DISPOSE);
    this.videoPlayer = null;
    this.videoElement = this.getVideoElement();
    this.playerContainer!.appendChild(this.videoElement);
    const eventPayload = {
      type: 'PLAYER_DISPOSED'
    };
    this.sendConvivaReportEvent(eventPayload);
    window.isPlayingEventSent = false;
  }

  addSeekButtons(): void {
    addSeekButtons(this.videoPlayer);
  }

  // on player load start listener function
  private onLoadStart() {
    // if (window && window.userInteracted) {
    //   this.videoPlayer.muted(false);
    //   this.videoPlayer.volume(1);
    // }
    this.emit(Events.LOAD_START);
    // Stop the autoplay logic if requested by a Bot
    if (this.isRequestedByBot) {
      return;
    }
    const promise = this.videoPlayer.play();
    if (typeof promise !== 'undefined') {
      promise
        .then(() => {
          const eventPayload = {
            type: 'AUTO_PLAY_WORKED'
          };
          this.sendConvivaReportEvent(eventPayload);
          console.log('Autoplay worked!');
        })
        .catch((error) => {
          // report user wait start event and maintain a flag saying wait event has started
          //retry if auto play fails by mute the player...
          this.sendUserWaitEvent(
            window.Conviva?.Constants.Events.USER_WAIT_STARTED || ''
          );
          this.autoPlayFailed = true;
          if (
            this.videoPlayer &&
            this.videoPlayer.player &&
            this.videoPlayer.player()
          ) {
            this.videoPlayer.muted(true);
            this.videoPlayer.volume(0);
            const promise = this.videoPlayer.play();
            if (typeof promise !== 'undefined') {
              promise
                .then(() => {
                  const eventPayload = {
                    type: 'AUTO_PLAY_RETRY_WORKED'
                  };
                  this.sendConvivaReportEvent(eventPayload);
                })
                .catch(() => {
                  const eventPayload = {
                    type: 'AUTO_PLAY_RETRY_NOT_WORKED'
                  };
                  this.sendConvivaReportEvent(eventPayload);
                });
            }
          }
        });
    }
  }

  // videojs player ready listener function
  private onPlayerReady() {
    this.setSource(this.mediaConfig);
    this.emit(Events.READY, this.videoPlayer);
    const eventPayload = {
      type: 'PLAYER_READY'
    };

    this.sendConvivaReportEvent(eventPayload);
    if (this.playerConfig.logo) this.renderWatermark(this.playerConfig.logo);
  }

  sendConvivaReportEvent(eventPayload: { type: string; payload?: any }) {
    if (
      window &&
      window.convivaAnalytic &&
      window.convivaAnalytic.reportEvent
    ) {
      window.convivaAnalytic.reportEvent('customEvent', eventPayload);
    }
  }

  sendUserWaitEvent(eventPayload: any) {
    if (
      window &&
      window.convivaAnalytic &&
      window.convivaAnalytic.reportEvent
    ) {
      window.convivaAnalytic.reportEvent(eventPayload);
    }
  }

  // videojs player mute listener function
  private onPlayerMute(event: any) {
    console.log(event.type);
    this.emit(Events.MUTE, this.videoPlayer);
  }

  // videojs player event listener function
  private onPlayerEvents(event: any) {
    // do nothing
  }

  // listening for player errors
  private onPlayerError(event: any) {
    const mediaError: MediaError = this.videoPlayer.error();
    if (mediaError.code == MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      console.log('SRC NOT SUPPORTED ERROR');
      console.error(mediaError);
      this.emit(
        Events.ERROR,
        new Error(
          ErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED,
          ErrorCategory.MEDIA,
          ErrorSeverity.CRITICAL,
          mediaError.message
        )
      );
    } else if (mediaError.code == MediaError.MEDIA_ERR_NETWORK) {
      console.log('NETWORK RELATED ERROR');
      console.log(mediaError);
      this.emit(
        Events.ERROR,
        new Error(
          ErrorCode.NETWORK,
          ErrorCategory.NETWORK,
          ErrorSeverity.RECOVERABLE,
          mediaError.message
        )
      );
    } else if (mediaError.code == MediaError.MEDIA_ERR_DECODE) {
      console.log('DECODER ERROR');
      console.log(mediaError);
      this.emit(
        Events.ERROR,
        new Error(
          ErrorCode.DECODER,
          ErrorCategory.MANIFEST,
          ErrorSeverity.CRITICAL,
          mediaError.message
        )
      );
    } else if (mediaError.code == MediaError.MEDIA_ERR_ABORTED) {
      console.log('MEDIA ABORTED');
      console.log(mediaError);
      this.emit(
        Events.ERROR,
        new Error(
          ErrorCode.ABORTED,
          ErrorCategory.MEDIA,
          ErrorSeverity.CRITICAL,
          mediaError.message
        )
      );
    } else {
      console.log('OTHER ERROR');
      console.log(mediaError);
      this.emit(
        Events.ERROR,
        new Error(
          ErrorCode.CUSTOM,
          ErrorCategory.STREAMING,
          ErrorSeverity.CRITICAL,
          mediaError.message
        )
      );
    }
  }

  // code for adding subtitle tracks
  addTextTracks(tracks: TextTrack[]): void {
    if (this.videoPlayer != undefined) {
      const oldTracks = this.videoPlayer.textTracks();
      let i = oldTracks.length;
      if (i) {
        while (i--) {
          if (oldTracks[i].kind === 'subtitles') {
            //remove tracks from manifest
            this.videoPlayer.removeRemoteTextTrack(oldTracks[i]);
          }
        }
      }
      if (tracks.length) {
        tracks.forEach((value) => {
          //adding tracks from spapi
          this.videoPlayer.addRemoteTextTrack(value);
        });
      }
    }
    this.registerTrackChange();
  }

  applySubtitleChangesOnAudioChange(
    tracks: TextTrack[],
    audioLanguage: string
  ): void {
    if (this.videoPlayer != undefined) {
      const oldTracks = this.videoPlayer.textTracks();
      let i = oldTracks.length;
      if (i) {
        while (i--) {
          if (oldTracks[i].kind === 'subtitles') {
            //remove tracks from manifest
            this.videoPlayer.removeRemoteTextTrack(oldTracks[i]);
          }
        }
      }

      if (tracks.length) {
        let subtitlePreference: { [key: string]: string } | null = null;
        let languageMatch = '';
        const storedSubtitlePreference =
          localStorage.getItem('subtitlePreference');
        if (storedSubtitlePreference !== null) {
          // Check if the value exists in localStorage
          try {
            subtitlePreference = JSON.parse(storedSubtitlePreference);
            if (subtitlePreference && subtitlePreference[audioLanguage]) {
              languageMatch = subtitlePreference[audioLanguage];
            }
          } catch (error) {
            console.error('Error parsing subtitlePreference:', error); // Handle parsing error
          }
        }
        tracks.forEach((value) => {
          if (
            languageMatch &&
            value['srclang'].toLowerCase() == languageMatch.toLowerCase()
          ) {
            value.mode = 'showing';
          }
          this.videoPlayer.addRemoteTextTrack(value);
        });
      }
    }
  }

  getActiveLanguageTrack() {
    let activeLanguageTrack = null;
    for (
      let index = 0;
      index < this.videoPlayer.audioTracks().tracks_.length;
      index++
    ) {
      if (this.videoPlayer.audioTracks().tracks_[index].enabled) {
        activeLanguageTrack =
          this.videoPlayer.audioTracks().tracks_[index].language || '';
        return activeLanguageTrack;
      }
    }
    return activeLanguageTrack;
  }

  getCastSender() {
    return this.castSender;
  }

  //map audio tracks language
  modifyAudioTracksLabel(
    audioLanguages: [] = [],
    streamLanguage?: any,
    entitledLanguages?: any,
    isEntitledLanguageFeatureEnabled?: boolean,
    multiAudioSelectionEnabled?: boolean,
    audioLanguageMapper?: any
  ) {
    if (this.videoPlayer != undefined) {
      const audioTracks = this.videoPlayer.audioTracks();
      if (streamLanguage && audioLanguages?.length > 0) {
        // fix for safari & mobile
        if (!audioTracks.length) {
          this.addDummyAudioTracks(streamLanguage, true);
        }
        audioLanguages.forEach((item: any) => {
          if (item !== streamLanguage) {
            this.addDummyAudioTracks(item);
          }
        });
        this.changeDefaultAudioLabel(
          audioTracks,
          streamLanguage,
          audioLanguageMapper
        );
      }

      // let playableTrackFound = false;
      for (let i = 0; i < audioTracks?.length; i++) {
        const track = audioTracks?.length && audioTracks[i];
        track.label =
          audioLanguageMapper[track?.language?.toLowerCase()] ||
          getLanguageName(track?.language);
        track?.language;
        // if (isEntitledLanguageFeatureEnabled) {
        //   if (
        //     entitledLanguages?.includes(track?.language) &&
        //     !playableTrackFound
        //   ) {
        //     track.enabled = true;
        //     playableTrackFound = true;
        //   } else {
        //     track.label = track.label;
        //     track.enabled = false;
        //   }
        //   if (playableTrackFound === false && !entitledLanguages?.length) {
        //     track.enabled = true;
        //     playableTrackFound = true;
        //   }
        // }
      }
      // if (isEntitledLanguageFeatureEnabled && getBrowserType() !== 'Safari') {
      //   if (!playableTrackFound) {
      //     this.emit(Events.ENTITLED_LANG_ERROR, this.videoPlayer);
      //   }
      // }

      audioTracks.on(
        'change',
        function action(this: any, entitledLanguages: any) {
          const audioTracks = this.videoPlayer.audioTracks();
          let audioLanguage = 'NA';
          let activeTrack: any = null;
          for (let i = 0; i < audioTracks.length; i++) {
            if (audioTracks[i].enabled) {
              activeTrack = audioTracks[i];
              audioLanguage = audioTracks[i].language;
              break;
            }
          }
          if (isEntitledLanguageFeatureEnabled) {
            this.emit(
              'audioChanged',
              this.videoPlayer,
              audioLanguage,
              audioLanguageMapper
            );
          }
        }.bind(this, entitledLanguages)
      );
    }
  }

  addDummyAudioTracks(item: any, enabled = false) {
    // Create a track object.
    const track = new videojs.AudioTrack({
      id: `${item}-audio-track`,
      kind: 'translation',
      label: item,
      language: item,
      enabled: enabled
    });

    // Add the track to the player's audio track list.
    this.videoPlayer.audioTracks().addTrack(track);
  }

  changeDefaultAudioLabel(
    audioTracks: string | any[],
    streamLanguage: any,
    audioLanguageMapper: any
  ) {
    for (let i = 0; i < audioTracks.length; i++) {
      const track = audioTracks[i];
      if (track.label === 'default') {
        track.language = streamLanguage;
        track.label =
          audioLanguageMapper[streamLanguage.toLowerCase()] ||
          getLanguageName(streamLanguage);
      }
    }
  }

  setVttThumbnailSrc(vttSrc: string) {
    this.videoPlayer.vttThumbnails({
      src: vttSrc
    });
  }

  //map audio tracks language
  private videoQualityChange = () => {
    const qualityLevels = this.videoPlayer.qualityLevels();
    qualityLevels.on('change', () => {
      if (
        qualityLevels &&
        qualityLevels.length >= qualityLevels.selectedIndex &&
        this.selectedQualityLevel !== qualityLevels.selectedIndex
      ) {
        const selectedQuality = getBitrateSelectedText(qualityLevels);
        if (selectedQuality) {
          this.selectedQualityLevel = qualityLevels.selectedIndex;
          this.emit(Events.QUALITY_CHANGE, selectedQuality);
          const eventPayload = {
            type: 'QUALITY_LEVEL_CHANGE',
            payload: JSON.stringify(selectedQuality)
          };
          this.sendConvivaReportEvent(eventPayload);
        }
      }
    });
  };

  registerTrackChange() {
    this.videoPlayer.textTracks().on('change', () => {
      this.videoPlayer.trigger('subtitleChanged');
    });
  }

  // code for adding watermark to the player
  private renderWatermark(logo: string) {
    const videoEl = this.videoPlayer.el();
    const div = document.createElement('div');
    const image = document.createElement('img');
    div.classList.add('vjs-watermark-content');
    image.src = logo;
    div.appendChild(image);
    videoEl.appendChild(div);
  }

  // videojs timeupdate event listener function
  private onTimeUpdate(event: any) {
    this.previousTime = this.currentTime;
    this.currentTime = this.videoPlayer.currentTime();
    this.emit(Events.TIME_UPDATE, this.videoPlayer);
  }

  // videojs seek event listener function
  private onSeekEvent(event: any) {
    const seekDetails = {
      from: this.seekStart,
      to: this.currentTime
    };
    this.seekStart = null;
    this.emit(Events.SEEKED, this.videoPlayer, seekDetails);
    const eventPayload = {
      type: 'ON_SEEK_EVENT'
    };
    this.sendConvivaReportEvent(eventPayload);
  }

  // videojs seek event listener function
  private onSeekingEvent(event: any) {
    if (this.seekStart === null) {
      this.seekStart = this.previousTime;
    }
  }

  // videojs volume change listener function
  private onVolumeChange(event: any) {
    this.emit(Events.VOLUME_CHANGE, this.videoPlayer);
  }

  // videojs metadata changed listener function
  private onMetaDataLoaded(event: any) {
    // console.log("VST:META_DATA_LOADED",new Date());
    // this.modifyAudioTracksLabel();
    this.videoQualityChange();
    this.emit(Events.META_DATA_LOADED, this.videoPlayer);
    const eventPayload = {
      type: 'META_DATA_LOADED'
    };
    this.sendConvivaReportEvent(eventPayload);
  }

  // videojs play event listener function
  private onPlayEvent(event: any) {
    this.emit(Events.PLAY, this.videoPlayer);
  }

  private onPauseEvent(event: any) {
    this.emit(Events.PAUSE, this.videoPlayer);
  }

  // videojs playback end listener function
  private onPlayEndedEvent(event: any) {
    this.emit(Events.ENDED, this.videoPlayer);
  }

  // videojs fullscreen toggle listener function
  private onFullScreenEvent(event: any) {
    this.emit(Events.FULL_SCREEN_CHANGE, this.videoPlayer);
  }

  // videojs subtitle change listener function
  private onSubtitleChange(event: any) {
    this.emit(Events.SUBTITLECHANGE, this.videoPlayer);
  }

  // videojs video progress listener function
  private onPlayerProgressEvent(event: any) {
    if (window && !window.isLive) {
      this.emit(Events.PROGRESS, this.videoPlayer);
    }
  }

  // videojs duration changed listener function
  private onDurationChanged(event: any) {
    const controlBar = this.videoPlayer.controlBar;
    const durationDisplay = controlBar.getChild('DurationDisplay');
    const currentTimeDisplay = controlBar.getChild('CurrentTimeDisplay');
    const timeDivider = controlBar.getChild('TimeDivider');
    let skipBackButton;
    let skipForButton;
    if (
      document.getElementsByClassName('vjs-skip-backward').length &&
      document.getElementsByClassName('vjs-skip-forward').length
    ) {
      skipBackButton = document.getElementsByClassName('vjs-skip-backward')[0];
      skipForButton = document.getElementsByClassName('vjs-skip-forward')[0];
    }
    if (
      this.videoPlayer.liveTracker.isLive() ||
      !isFinite(this.videoPlayer.duration())
    ) {
      durationDisplay.show();
      currentTimeDisplay.show();
      timeDivider.show();
      if (skipBackButton && skipForButton) {
        skipBackButton.remove();
        skipForButton.remove();
      }
    } else {
      durationDisplay.show();
      currentTimeDisplay.show();
      timeDivider.show();
    }
  }

  destroy(): void {
    if (this.videoPlayer != undefined) {
      if (this.adsManager) this.adsManager.destroy();
      this.castSender = null;
      // commenting for an error - Avinash - 25May
      // this.videoPlayer.reset()
      this.deregisterEvents();
      this.videoPlayer.dispose();
      this.videoPlayer = null;
    }
  }

  deregisterEvents() {
    this.videoPlayer.off('ready', this.onPlayerReady.bind(this));
    this.videoPlayer.off('loadstart', this.onLoadStart.bind(this));
    this.videoPlayer.off('ended', this.onPlayEndedEvent.bind(this));
    this.videoPlayer.off('error', this.onPlayerError.bind(this));
    this.videoPlayer.off('loadeddata', this.onPlayerEvents.bind(this));
    this.videoPlayer.off('loadedmetadata', this.onMetaDataLoaded.bind(this));
    this.videoPlayer.off('timeupdate', this.onTimeUpdate.bind(this));
    this.videoPlayer.off('volumechange', this.onVolumeChange.bind(this));
    this.videoPlayer.off('suspend', this.onPlayerEvents.bind(this));
    this.videoPlayer.off('emptied', this.onPlayerEvents.bind(this));
    this.videoPlayer.off('stalled', this.onPlayerEvents.bind(this));
    this.videoPlayer.off('resize', this.onPlayerEvents.bind(this));
    this.videoPlayer.off('durationchange', this.onDurationChanged.bind(this));
    this.videoPlayer.off('play', this.onPlayEvent.bind(this));
    this.videoPlayer.off('fullscreenchange', this.onFullScreenEvent.bind(this));
    this.videoPlayer.off('seeked', this.onSeekEvent.bind(this));
    this.videoPlayer.off('seeking', this.onSeekingEvent.bind(this));
    this.videoPlayer.off('pause', this.onPauseEvent.bind(this));
    this.videoPlayer.off('subtitleChanged', this.onSubtitleChange.bind(this));
    this.videoPlayer.off('progress', this.onPlayerProgressEvent.bind(this));
    this.videoPlayer.off('dispose', this.onPlayerDisposeEvent.bind(this));
    this.videoPlayer.off('mute', this.onPlayerMute.bind(this));
    this.videoPlayer.off('dblclick', this.onPlayerDblClick.bind(this));
    this.videoPlayer.off('useractive', this.userActive.bind(this));
    this.videoPlayer.off('userinactive', this.userInactive.bind(this));
  }

  end(): void {
    if (this.videoPlayer != undefined) {
      this.videoPlayer.pause();
      this.videoPlayer.currentTime = 0;
    }
  }

  enterFullScreen(): void {
    if (this.videoPlayer != undefined && !this.videoPlayer.isFullscreen()) {
      this.videoPlayer.requestFullscreen();
    }
  }

  exitFullScreen(): void {
    if (this.videoPlayer != undefined && this.videoPlayer.isFullscreen()) {
      this.videoPlayer.exitFullscreen();
    }
  }

  enterPictureInPicture(): void {
    if (document.pictureInPictureEnabled) {
      this.videoPlayer.requestPictureInPicture();
    }
  }

  exitPictureInPicture(): void {
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
    }
  }

  goLive(): void {
    if (this.videoPlayer != undefined && this.videoPlayer.isLive()) {
      this.videoPlayer.seekToLiveEdge();
    }
  }

  mute(): void {
    if (this.videoPlayer != undefined) {
      this.videoPlayer.muted(true);
    }
  }

  unmute(): void {
    if (this.videoPlayer != undefined) {
      this.videoPlayer.muted(false);
    }
  }

  getMutedState(): boolean | undefined {
    if (this.videoPlayer != undefined) {
      return (
        this.videoPlayer.el != undefined &&
        this.videoPlayer.muted &&
        this.videoPlayer.muted()
      );
    }
  }

  pause(): void {
    if (this.videoPlayer != undefined) {
      if (this.adsManager && this.adsManager.isAdBreak()) {
        this.adsManager.pauseAd();
        return;
      }
      this.videoPlayer.pause();
    }
  }

  play(): void {
    if (this.videoPlayer != undefined) {
      if (this.adsManager && this.adsManager.isAdBreak()) {
        this.adsManager.resumeAd();
        return;
      }
      this.videoPlayer.play();
    }
  }

  replay(): void {
    if (this.videoPlayer != undefined) {
      this.videoPlayer.currentTime = 0;
      this.videoPlayer.play();
    }
  }

  seek(value: number): void {
    if (this.videoPlayer != undefined) {
      this.videoPlayer.currentTime = value;
    }
  }
}
